import { pinField, pinFieldAny, asNumber } from "./pinFields";

// ═══════════════════════════════════════════════════════════════════════════
// MAP FILTER PREDICATES
// ═══════════════════════════════════════════════════════════════════════════
// Pure predicates for the Location and Filters panels, kept out of MapView so
// the marker effect and the list view can share one definition.
//
// Every filter is skipped when empty, so an untouched panel never removes
// anything. Where a pin has no value for a field being filtered on, it is
// excluded — the panels surface how many pins that affects so a selection
// can't silently hide a whole category.

// Field aliases per concept, since the same idea has different column names
// across properties / deals / comps.
export const LOCATION_FIELDS = {
  market:    ["market"],
  submarket: ["submarket", "micro_market"],
  city:      ["city"],
};

export function pinLocationValue(pin, field) {
  return pinFieldAny(pin, LOCATION_FIELDS[field] || [field]);
}

// selected: { market: Set, submarket: Set, city: Set } — all case-insensitive.
export function pinPassesLocation(pin, selected) {
  if (!selected) return true;
  for (const field of Object.keys(LOCATION_FIELDS)) {
    const set = selected[field];
    if (!set || set.size === 0) continue;
    const v = pinLocationValue(pin, field);
    if (v == null) return false;
    if (!set.has(String(v).trim())) return false;
  }
  return true;
}

// Distinct values for a location field across a pin set, plus a count of pins
// that have no value — that count is what the panel shows as a warning.
export function locationOptions(pins, field) {
  const seen = new Map();   // lowercased → original casing
  let missing = 0;
  (pins || []).forEach(p => {
    const v = pinLocationValue(p, field);
    if (v == null) { missing += 1; return; }
    const t = String(v).trim();
    if (!t) { missing += 1; return; }
    if (!seen.has(t.toLowerCase())) seen.set(t.toLowerCase(), t);
  });
  return {
    values: [...seen.values()].sort((a, b) => a.localeCompare(b)),
    missing,
  };
}

export const EMPTY_ATTR_FILTERS = {
  sfMin: "", sfMax: "",
  yearMin: "", yearMax: "",
  capMin: "", capMax: "",
  leaseTypes: [],     // "NNN" | "Gross" | "Modified Gross"
};

// How many attribute filters are actually narrowing anything — drives the
// count badge on the Filters button.
export function activeAttrCount(f) {
  if (!f) return 0;
  let n = 0;
  if (f.sfMin !== "" || f.sfMax !== "") n += 1;
  if (f.yearMin !== "" || f.yearMax !== "") n += 1;
  if (f.capMin !== "" || f.capMax !== "") n += 1;
  if (f.leaseTypes && f.leaseTypes.length) n += 1;
  return n;
}

export function activeLocationCount(selected) {
  if (!selected) return 0;
  return Object.keys(LOCATION_FIELDS)
    .reduce((n, k) => n + (selected[k] && selected[k].size > 0 ? 1 : 0), 0);
}

function inRange(value, min, max) {
  const n = asNumber(value);
  const lo = asNumber(min), hi = asNumber(max);
  if (lo == null && hi == null) return true;
  if (n == null) return false;         // filtering on a field the pin lacks
  if (lo != null && n < lo) return false;
  if (hi != null && n > hi) return false;
  return true;
}

// Square footage lives under different names per table.
const SF_KEYS = ["sf", "rba", "square_footage", "building_sf"];

export function pinPassesAttrFilters(pin, f) {
  if (!f) return true;

  if (f.sfMin !== "" || f.sfMax !== "") {
    if (!inRange(pinFieldAny(pin, SF_KEYS), f.sfMin, f.sfMax)) return false;
  }

  if (f.yearMin !== "" || f.yearMax !== "") {
    // year_built is text in places ("1981-1986", "1977 / 2021") — take the
    // first 4-digit run so a range string still filters sensibly.
    const raw = pinField(pin, "year_built");
    const m = raw == null ? null : String(raw).match(/\d{4}/);
    if (!inRange(m ? m[0] : null, f.yearMin, f.yearMax)) return false;
  }

  if (f.capMin !== "" || f.capMax !== "") {
    if (!inRange(pinFieldAny(pin, ["cap_rate", "capRate"]), f.capMin, f.capMax)) return false;
  }

  if (f.leaseTypes && f.leaseTypes.length > 0) {
    const t = pinField(pin, "lease_type");
    if (t == null) return false;
    const norm = String(t).trim().toLowerCase();
    const match = f.leaseTypes.some(sel => {
      const s = sel.toLowerCase();
      if (s === "modified gross") return norm === "modified gross" || norm === "mg";
      return norm === s;
    });
    if (!match) return false;
  }

  return true;
}
